import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Github,
  CheckCircle,
  Plus,
  Loader2,
  Pause,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  XCircle,
} from 'lucide-react';
import { apiClient, GitHubIntegrationStatus, GitHubRepositoriesResponse, GitLabIntegrationStatus, GitLabRepositoriesResponse, GitLabProject, IaCRepoConfig } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { isAdminOrOps } from '../utils/roles';

// Unified cloud connection type for the dropdown
interface CloudConnection {
  id: string;
  type: 'aws' | 'oci';
  displayName: string;
  accountId: string;
  region: string;
  status: string;
}

const CICDIntegration: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // GitHub integration state
  const [githubStatus, setGithubStatus] = useState<GitHubIntegrationStatus | null>(null);
  const [githubRepositories, setGithubRepositories] = useState<GitHubRepositoriesResponse | null>(null);
  const [loadingGithub, setLoadingGithub] = useState(false);
  const [connectingGithub, setConnectingGithub] = useState(false);
  const [showGithubRepositories, setShowGithubRepositories] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // GitLab integration state
  const [gitlabStatus, setGitlabStatus] = useState<GitLabIntegrationStatus | null>(null);
  const [gitlabRepositories, setGitlabRepositories] = useState<GitLabRepositoriesResponse | null>(null);
  const [gitlabProjects, setGitlabProjects] = useState<GitLabProject[]>([]);
  const [loadingGitlab, setLoadingGitlab] = useState(false);
  const [connectingGitlab, setConnectingGitlab] = useState(false);
  const [disconnectingGitlab, setDisconnectingGitlab] = useState(false);
  const [showDisconnectGitlabDialog, setShowDisconnectGitlabDialog] = useState(false);
  const [showGitlabRepositories, setShowGitlabRepositories] = useState(false);
  const [showGitlabProjectSelector, setShowGitlabProjectSelector] = useState(false);
  const [syncingGitlabProjects, setSyncingGitlabProjects] = useState(false);
  const [selectedGitlabProjects, setSelectedGitlabProjects] = useState<number[]>([]);

  // IaC configuration state
  const [iacRepos, setIacRepos] = useState<IaCRepoConfig[]>([]);
  const [cloudConnections, setCloudConnections] = useState<CloudConnection[]>([]);
  const [showIaCConfig, setShowIaCConfig] = useState<string | false>(false);
  const [configuringIaC, setConfiguringIaC] = useState<string | null>(null);
  const [selectedIaCType, setSelectedIaCType] = useState<string>('terraform');
  const [selectedRootPath, setSelectedRootPath] = useState<string>('/');
  const [selectedCloudConnection, setSelectedCloudConnection] = useState<string>('');

  const loadGithubStatus = async () => {
    setLoadingGithub(true);
    try {
      const status = await apiClient.getGitHubIntegrationStatus();
      setGithubStatus(status);

      // If connected, also load repositories
      if (status.organization) {
        try {
          const repos = await apiClient.getGitHubRepositories();
          setGithubRepositories(repos);
        } catch (repoErr) {
          console.error('Failed to fetch GitHub repositories:', repoErr);
          // Clear repositories if we can't fetch them (e.g., when suspended)
          setGithubRepositories(null);
        }
      }
      return status;
    } catch (err) {
      console.error('Failed to fetch GitHub status:', err);
      // Set empty status if not connected
      const emptyStatus = {
        is_connected: false,
        has_valid_token: false,
        organization: null,
        app_name: 'kestrelai',
        repository_count: 0,
        enabled_repo_count: 0,
      };
      setGithubStatus(emptyStatus);
      return emptyStatus;
    } finally {
      setLoadingGithub(false);
    }
  };

  const handleConnectGithub = async () => {
    setConnectingGithub(true);
    setError('');

    try {
      const response = await apiClient.connectGitHubIntegration();
      // Open GitHub App installation in new tab
      window.open(response.installation_url, '_blank');

      // Poll for successful connection
      let attempts = 0;
      const maxAttempts = 30; // 30 seconds of polling
      const pollInterval = setInterval(async () => {
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setConnectingGithub(false);
          setError('Connection timeout. Please try again or contact support.');
          return;
        }

        try {
          const status = await loadGithubStatus();
          if (status && status.organization) {
            clearInterval(pollInterval);
            setConnectingGithub(false);
            setSuccess('GitHub integration connected successfully!');
            // Clear success message after 5 seconds
            setTimeout(() => setSuccess(''), 5000);
          }
        } catch (error) {
          console.error('Error polling GitHub status:', error);
        }
      }, 1000);

      // Clean up interval on component unmount
    } catch (err: any) {
      setError(err.message || 'Failed to initiate GitHub connection');
      setConnectingGithub(false);
    }
  };

  const handleGithubCardClick = () => {
    if (githubStatus?.status === 'active' || githubStatus?.status === 'suspended') {
      setShowGithubRepositories(!showGithubRepositories);
    }
  };

  // GitLab integration functions
  const loadGitlabStatus = async () => {
    setLoadingGitlab(true);
    try {
      const status = await apiClient.getGitLabIntegrationStatus();
      setGitlabStatus(status);

      // If connected, also load repositories
      if (status.is_connected) {
        try {
          const repos = await apiClient.getGitLabRepositories();
          setGitlabRepositories(repos);
        } catch (repoErr) {
          console.error('Failed to fetch GitLab repositories:', repoErr);
          setGitlabRepositories(null);
        }
      }
      return status;
    } catch (err) {
      console.error('Failed to fetch GitLab status:', err);
      const emptyStatus: GitLabIntegrationStatus = {
        is_connected: false,
        has_valid_token: false,
        username: null,
        repository_count: 0,
        enabled_repo_count: 0,
      };
      setGitlabStatus(emptyStatus);
      return emptyStatus;
    } finally {
      setLoadingGitlab(false);
    }
  };

  const handleConnectGitlab = async () => {
    setConnectingGitlab(true);
    setError('');

    try {
      const response = await apiClient.connectGitLabIntegration();
      // Open GitLab authorization in new tab
      window.open(response.authorization_url, '_blank');

      // Poll for successful connection
      let attempts = 0;
      const maxAttempts = 60; // 60 seconds of polling
      const pollInterval = setInterval(async () => {
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setConnectingGitlab(false);
          setError('Connection timeout. Please try again or contact support.');
          return;
        }

        try {
          const status = await loadGitlabStatus();
          if (status && status.is_connected) {
            clearInterval(pollInterval);
            setConnectingGitlab(false);
            setSuccess('GitLab integration connected successfully!');
            setTimeout(() => setSuccess(''), 5000);
          }
        } catch (error) {
          console.error('Error polling GitLab status:', error);
        }
      }, 1000);

    } catch (err: any) {
      setError(err.message || 'Failed to initiate GitLab connection');
      setConnectingGitlab(false);
    }
  };

  const confirmDisconnectGitlab = async () => {
    setDisconnectingGitlab(true);
    try {
      await apiClient.disconnectGitLabIntegration();
      setSuccess('GitLab disconnected successfully');
      setTimeout(() => setSuccess(''), 5000);
      setGitlabStatus(null);
      setGitlabRepositories(null);
      setShowGitlabRepositories(false);
      setShowDisconnectGitlabDialog(false);
      await loadGitlabStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect GitLab');
    } finally {
      setDisconnectingGitlab(false);
    }
  };

  const handleGitlabCardClick = () => {
    if (gitlabStatus?.is_connected) {
      setShowGitlabRepositories(!showGitlabRepositories);
    }
  };

  const loadGitlabProjects = async () => {
    try {
      const response = await apiClient.getGitLabProjects();
      setGitlabProjects(response.projects || []);
    } catch (err) {
      console.error('Failed to load GitLab projects:', err);
    }
  };

  const handleSyncGitlabProjects = async () => {
    if (selectedGitlabProjects.length === 0) {
      setError('Please select at least one project to sync');
      return;
    }

    setSyncingGitlabProjects(true);
    try {
      const response = await apiClient.syncGitLabRepositories(selectedGitlabProjects);
      if (response.errors && response.errors.length > 0) {
        setError(`Synced ${response.synced_count} projects with ${response.errors.length} errors`);
      } else {
        setSuccess(`Successfully synced ${response.synced_count} projects`);
        setTimeout(() => setSuccess(''), 5000);
      }
      setSelectedGitlabProjects([]);
      setShowGitlabProjectSelector(false);
      await loadGitlabStatus();
      await loadIaCData();
    } catch (err: any) {
      setError(err.message || 'Failed to sync GitLab projects');
    } finally {
      setSyncingGitlabProjects(false);
    }
  };

  const toggleGitlabProjectSelection = (projectId: number) => {
    setSelectedGitlabProjects(prev => 
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  // Load IaC repos and cloud connections (AWS + OCI)
  const loadIaCData = async () => {
    try {
      const iacData = await apiClient.listIaCRepos();
      setIacRepos(iacData.iac_repos || []);
      
      const allConnections: CloudConnection[] = [];
      const seenAccountIds = new Set<string>();
      
      // Try to load AWS connections
      try {
        const awsData = await apiClient.getAWSConnectionStatus('cloud-ingestion');
        if (awsData.connections && awsData.connections.length > 0) {
          for (const conn of awsData.connections) {
            // Dedupe by account_id + region
            const key = `aws-${conn.account_id}-${conn.region}`;
            if (!seenAccountIds.has(key)) {
              seenAccountIds.add(key);
              allConnections.push({
                id: conn.id,
                type: 'aws',
                displayName: `AWS: ${conn.account_id}`,
                accountId: conn.account_id,
                region: conn.region,
                status: conn.connection_status,
              });
            }
          }
        }
      } catch (awsErr) {
        // AWS connections not configured - that's fine
        console.log('AWS connections not available');
      }
      
      // Try to load OCI connections
      try {
        const ociData = await apiClient.getOCIConnectionStatus();
        if (ociData.connections && ociData.connections.length > 0) {
          for (const conn of ociData.connections) {
            // Dedupe by tenancy_ocid + region
            const key = `oci-${conn.tenancy_ocid}-${conn.region}`;
            if (!seenAccountIds.has(key)) {
              seenAccountIds.add(key);
              // Extract short tenancy ID from OCID for display
              const tenancyShort = conn.tenancy_ocid.split('.').pop()?.substring(0, 12) || conn.tenancy_ocid;
              allConnections.push({
                id: conn.id,
                type: 'oci',
                displayName: conn.connection_name || `OCI: ${tenancyShort}...`,
                accountId: conn.tenancy_ocid,
                region: conn.region,
                status: conn.connection_status,
              });
            }
          }
        }
      } catch (ociErr) {
        // OCI connections not configured - that's fine
        console.log('OCI connections not available');
      }
      
      setCloudConnections(allConnections);
      if (allConnections.length > 0) {
        setSelectedCloudConnection(allConnections[0].id);
      }
    } catch (err) {
      console.log('IaC data not available:', err);
    }
  };

  // Enable IaC for a repository
  const handleEnableIaC = async (repositoryId: string) => {
    setConfiguringIaC(repositoryId);
    try {
      await apiClient.enableIaCRepo(
        repositoryId,
        selectedIaCType,
        selectedRootPath,
        selectedCloudConnection || undefined
      );
      setSuccess('IaC configuration enabled successfully!');
      setTimeout(() => setSuccess(''), 5000);
      await loadIaCData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to enable IaC configuration');
    } finally {
      setConfiguringIaC(null);
    }
  };

  // Disable IaC for a repository
  const handleDisableIaC = async (repositoryId: string) => {
    setConfiguringIaC(repositoryId);
    try {
      await apiClient.disableIaCRepo(repositoryId);
      setSuccess('IaC configuration disabled');
      setTimeout(() => setSuccess(''), 5000);
      await loadIaCData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable IaC configuration');
    } finally {
      setConfiguringIaC(null);
    }
  };

  // Check if a repository is configured for IaC
  const getIaCConfigForRepo = (repositoryId: string): IaCRepoConfig | undefined => {
    return iacRepos.find((r) => r.repository_id === repositoryId);
  };

  // Load integration status on component mount
  useEffect(() => {
    loadGithubStatus();
    loadGitlabStatus();
    loadIaCData();
  }, []);

  // Handle GitLab OAuth callback query parameters
  useEffect(() => {
    const gitlabConnected = searchParams.get('gitlab_connected');
    const gitlabError = searchParams.get('gitlab_error');

    if (gitlabConnected === 'true') {
      setSuccess('GitLab integration connected successfully!');
      setTimeout(() => setSuccess(''), 5000);
      // Reload GitLab status to show the new connection
      loadGitlabStatus();
      // Clear the query params
      setSearchParams({});
    } else if (gitlabError) {
      setError(`GitLab connection failed: ${gitlabError}`);
      // Clear the query params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Show loading state while authentication is loading
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Only allow org_admins and ops_admins to access this page
  if (!isAdminOrOps(user?.role)) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Access denied. Only organization administrators or platform administrators can manage CI/CD integrations.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* GitHub Integration Card */}
        <Card
          className="border-2 hover:border-primary/50 transition-all duration-200 cursor-pointer group hover:shadow-md"
          onClick={handleGithubCardClick}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-900 dark:bg-gray-100 rounded-lg flex items-center justify-center">
                  <Github className="h-6 w-6 text-white dark:text-gray-900" />
                </div>
                <div>
                  <h3 className="font-semibold">GitHub</h3>
                  <p className="text-sm text-muted-foreground">Repository management</p>
                </div>
              </div>
              {loadingGithub ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : githubStatus?.status === 'active' ? (
                <CheckCircle className="h-5 w-5 text-foreground" />
              ) : githubStatus?.status === 'suspended' ? (
                <Pause className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              ) : (
                <Plus className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              )}
            </div>

            {(githubStatus?.status === 'active' || githubStatus?.status === 'suspended') ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Organization:</span>
                  <span className="font-medium">{githubStatus.organization}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className={`font-medium capitalize ${
                    githubStatus.status === 'active' ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {githubStatus.status === 'suspended' ? 'Suspended by GitHub' : githubStatus.status}
                  </span>
                </div>
                {githubStatus.status === 'suspended' ? (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 pt-2">
                    Your GitHub App has been suspended. Please check your GitHub App settings to reactivate it.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground pt-2">
                    Manage repositories and permissions in your GitHub App installation settings.
                  </p>
                )}
                <div className="flex items-center justify-center pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span>
                      {showGithubRepositories ? 'Hide repositories' : 'View repositories'}
                    </span>
                    {showGithubRepositories ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Connect GitHub to manage repositories and enable automated workflows.
                </p>
                <Button
                  onClick={handleConnectGithub}
                  disabled={connectingGithub}
                  className="w-full"
                  size="sm"
                >
                  {connectingGithub ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <Github className="h-3 w-3 mr-2" />
                  )}
                  Connect GitHub
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* GitLab Integration Card */}
        <Card
          className="border-2 hover:border-primary/50 transition-all duration-200 cursor-pointer group hover:shadow-md"
          onClick={handleGitlabCardClick}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-900 dark:bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="h-6 w-6 text-white dark:text-gray-900"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-label="GitLab logo"
                    role="img"
                  >
                    <path d="M12.1 21.8l3.7-11.4h-7.4L12.1 21.8zM12.1 21.8l-3.7-11.4H1.6L12.1 21.8zM1.6 10.4L.2 14.6c-.1.4 0 .8.3 1l11.6 7.2l-10.5-12.4zM1.6 10.4h6.8l-2.2-6.8c-.2-.6-.8-.6-1 0L1.6 10.4zM12.1 21.8l3.7-11.4h6.8L12.1 21.8zM22.4 10.4l1.4 4.2c.1.4 0 .8-.3 1L12 22.8l10.4-12.4zM22.4 10.4h-6.8l2.2-6.8c.2-.6.8-.6 1 0l3.6 6.8z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">GitLab</h3>
                  <p className="text-sm text-muted-foreground">Repository management</p>
                </div>
              </div>
              {loadingGitlab ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : gitlabStatus?.is_connected ? (
                <CheckCircle className="h-5 w-5 text-foreground" />
              ) : (
                <Plus className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              )}
            </div>

            {gitlabStatus?.is_connected ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Username:</span>
                  <span className="font-medium">{gitlabStatus.username}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Repositories:</span>
                  <span className="font-medium">{gitlabStatus.repository_count}</span>
                </div>
                <p className="text-xs text-muted-foreground pt-2">
                  Manage repositories and sync projects from your GitLab account.
                </p>
                <div className="flex items-center justify-center pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span>
                      {showGitlabRepositories ? 'Hide repositories' : 'View repositories'}
                    </span>
                    {showGitlabRepositories ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Connect GitLab to manage repositories and enable automated workflows.
                </p>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnectGitlab();
                  }}
                  disabled={connectingGitlab}
                  className="w-full bg-foreground text-background hover:bg-foreground/90"
                  size="sm"
                >
                  {connectingGitlab ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <svg className="h-3 w-3 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.1 21.8l3.7-11.4h-7.4L12.1 21.8zM12.1 21.8l-3.7-11.4H1.6L12.1 21.8zM1.6 10.4L.2 14.6c-.1.4 0 .8.3 1l11.6 7.2l-10.5-12.4zM1.6 10.4h6.8l-2.2-6.8c-.2-.6-.8-.6-1 0L1.6 10.4zM12.1 21.8l3.7-11.4h6.8L12.1 21.8zM22.4 10.4l1.4 4.2c.1.4 0 .8-.3 1L12 22.8l10.4-12.4zM22.4 10.4h-6.8l2.2-6.8c.2-.6.8-.6 1 0l3.6 6.8z"/>
                    </svg>
                  )}
                  Connect GitLab
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Connected Repositories Information (read-only) - only show when user clicks GitHub integration */}
      {showGithubRepositories && (githubStatus?.status === 'active' || githubStatus?.status === 'suspended') && githubRepositories && (
        <Card>
          <CardHeader>
            <CardTitle>GitHub Repositories</CardTitle>
            <CardDescription>
              Repository access is managed in your GitHub App installation. Add or remove repositories from your GitHub organization settings.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Read-only repository list with IaC configuration */}
            <div className="space-y-3 max-h-[60vh] overflow-y-auto border rounded-lg p-4 bg-muted/30">
              {githubRepositories.repositories.map((repo) => {
                const iacConfig = getIaCConfigForRepo(repo.id);
                return (
                <div key={repo.id} className="flex flex-col p-4 border rounded-lg bg-background overflow-hidden">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <h4 className="font-medium break-all">{repo.repo_full_name}</h4>
                      {repo.is_private && (
                        <Badge variant="secondary" className="text-xs">Private</Badge>
                      )}
                      {iacConfig && (
                        <Badge variant="default" className="text-xs bg-zinc-700 hover:bg-zinc-700 dark:bg-zinc-300 dark:text-zinc-900">
                          {iacConfig.iac_type.charAt(0).toUpperCase() + iacConfig.iac_type.slice(1)}
                        </Badge>
                      )}
                    </div>
                    
                    {/* IaC Toggle Button */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {iacConfig ? (
                        <Badge variant="outline" className="text-xs">
                          Enabled
                        </Badge>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowIaCConfig(showIaCConfig === repo.id ? false : repo.id);
                          }}
                        >
                          Configure IaC
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {repo.github_metadata?.description || 'No description available'}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <span>Default branch: <span className="font-medium">{repo.default_branch}</span></span>
                    {repo.github_metadata?.language && (
                      <span>• <span className="font-medium">{repo.github_metadata.language}</span></span>
                    )}
                  </div>

                  {/* IaC Config Info */}
                  {iacConfig && (
                    <div className="mt-3 p-2.5 bg-muted/50 rounded-lg border border-border/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="font-medium">IaC Configuration</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDisableIaC(repo.id);
                          }}
                          disabled={configuringIaC === repo.id}
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
                          title="Remove IaC configuration"
                        >
                          {configuringIaC === repo.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Type:</span>
                          <span className="font-medium text-foreground capitalize">{iacConfig.iac_type}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Root Path:</span>
                          <code className="font-medium text-foreground bg-muted/50 px-1.5 py-0.5 rounded text-[11px]">{iacConfig.iac_root_path}</code>
                        </div>
                        {iacConfig.last_analyzed_at && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">Last Analyzed:</span>
                            <span className="font-medium text-foreground">{new Date(iacConfig.last_analyzed_at).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* IaC Configuration Form */}
                  {showIaCConfig === repo.id && !iacConfig && (
                    <div className="mt-3 p-4 bg-muted/50 rounded-md border border-border">
                      <div className="mb-3 text-sm font-medium text-foreground">
                        Configure as IaC Repository
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium">IaC Type</label>
                          <Select
                            value={selectedIaCType}
                            onValueChange={setSelectedIaCType}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select IaC type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="terraform">Terraform</SelectItem>
                              <SelectItem value="cloudformation">CloudFormation</SelectItem>
                              <SelectItem value="pulumi">Pulumi</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-medium">Root Path</label>
                          <Input
                            value={selectedRootPath}
                            onChange={(e) => setSelectedRootPath(e.target.value)}
                            placeholder="e.g., /terraform or /"
                            className="mt-1"
                          />
                        </div>
                        {cloudConnections.length > 0 && (
                          <div>
                            <label className="text-xs font-medium">Cloud Account (Optional)</label>
                            <Select
                              value={selectedCloudConnection || 'none'}
                              onValueChange={(val) => setSelectedCloudConnection(val === 'none' ? '' : val)}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select cloud account" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {cloudConnections.map((conn) => (
                                  <SelectItem key={conn.id} value={conn.id}>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                        conn.type === 'aws' 
                                          ? 'bg-muted text-foreground' 
                                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      }`}>
                                        {conn.type.toUpperCase()}
                                      </span>
                                      {conn.displayName} ({conn.region})
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-2">
                          <Button
                            onClick={() => handleEnableIaC(repo.id)}
                            disabled={configuringIaC === repo.id}
                            size="sm"
                            className="bg-foreground text-background hover:bg-foreground/90"
                          >
                            {configuringIaC === repo.id ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Configuring...
                              </>
                            ) : (
                              <>Enable IaC</>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowIaCConfig(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
              })}

              {githubRepositories.repositories.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Github className="h-16 w-16 mx-auto mb-4 opacity-40" />
                  <p className="text-lg font-medium mb-2">No repositories connected</p>
                  <p className="text-sm">Add repositories to your GitHub App installation to see them here.</p>
                </div>
              )}
            </div>

            {githubRepositories.repositories.length > 0 && (
              <div className="text-xs text-muted-foreground text-center pt-2">
                {githubRepositories.repositories.length} repositories connected • Manage access in GitHub settings
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* GitLab Connected Repositories - only show when user clicks GitLab integration */}
      {showGitlabRepositories && gitlabStatus?.is_connected && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>GitLab Repositories</CardTitle>
                <CardDescription>
                  Sync projects from your GitLab account to enable IaC configuration and merge request creation.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await loadGitlabProjects();
                    setShowGitlabProjectSelector(true);
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Projects
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDisconnectGitlabDialog(true);
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Disconnect
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Project Selector Modal */}
            {showGitlabProjectSelector && (
              <div className="mb-4 p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Select GitLab Projects to Sync</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowGitlabProjectSelector(false);
                      setSelectedGitlabProjects([]);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {gitlabProjects.filter((project) => {
                    // Hide projects that are already synced
                    const alreadySynced = gitlabRepositories?.repositories?.some(
                      (r) => r.gitlab_project_id === project.id
                    );
                    return !alreadySynced;
                  }).map((project) => (
                    <div
                      key={project.id}
                      className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedGitlabProjects.includes(project.id)
                          ? 'bg-muted border-foreground/50'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleGitlabProjectSelection(project.id)}
                    >
                      <div>
                        <div className="font-medium">{project.full_path}</div>
                        <div className="text-xs text-muted-foreground">
                          {project.description || 'No description'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {project.is_private && (
                          <Badge variant="secondary" className="text-xs">Private</Badge>
                        )}
                        {selectedGitlabProjects.includes(project.id) && (
                          <CheckCircle className="h-4 w-4 text-foreground" />
                        )}
                      </div>
                    </div>
                  ))}
                  {gitlabProjects.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No projects found. Make sure you have access to GitLab projects.</p>
                    </div>
                  )}
                  {gitlabProjects.length > 0 && gitlabProjects.every((p) => 
                    gitlabRepositories?.repositories?.some((r) => r.gitlab_project_id === p.id)
                  ) && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>All available projects have already been synced.</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end mt-3">
                  <Button
                    onClick={handleSyncGitlabProjects}
                    disabled={selectedGitlabProjects.length === 0 || syncingGitlabProjects}
                    className="bg-foreground text-background hover:bg-foreground/90"
                  >
                    {syncingGitlabProjects ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        Sync {selectedGitlabProjects.length} Project{selectedGitlabProjects.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Repository list */}
            <div className="space-y-3 max-h-[60vh] overflow-y-auto border rounded-lg p-4 bg-muted/30">
              {(gitlabRepositories?.repositories || []).map((repo) => {
                const iacConfig = getIaCConfigForRepo(repo.id);
                return (
                <div key={repo.id} className="flex flex-col p-4 border rounded-lg bg-background overflow-hidden">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <h4 className="font-medium break-all">{repo.project_path}</h4>
                      {repo.visibility === 'private' && (
                        <Badge variant="secondary" className="text-xs">Private</Badge>
                      )}
                      {iacConfig && (
                        <Badge variant="default" className="text-xs bg-zinc-700 hover:bg-zinc-700 dark:bg-zinc-300 dark:text-zinc-900">
                          {iacConfig.iac_type.charAt(0).toUpperCase() + iacConfig.iac_type.slice(1)}
                        </Badge>
                      )}
                    </div>
                    
                    {/* IaC Toggle Button */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {iacConfig ? (
                        <Badge variant="outline" className="text-xs">
                          Enabled
                        </Badge>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowIaCConfig(showIaCConfig === repo.id ? false : repo.id);
                          }}
                        >
                          Configure IaC
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {repo.gitlab_metadata?.description || 'No description available'}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <span>Default branch: <span className="font-medium">{repo.default_branch}</span></span>
                    <span>• <span className="font-medium">{repo.visibility}</span></span>
                  </div>

                  {/* IaC Config Info */}
                  {iacConfig && (
                    <div className="mt-3 p-2.5 bg-muted/50 rounded-lg border border-border/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="font-medium">IaC Configuration</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDisableIaC(repo.id);
                          }}
                          disabled={configuringIaC === repo.id}
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
                          title="Remove IaC configuration"
                        >
                          {configuringIaC === repo.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Type:</span>
                          <span className="font-medium text-foreground capitalize">{iacConfig.iac_type}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Root Path:</span>
                          <code className="font-medium text-foreground bg-muted/50 px-1.5 py-0.5 rounded text-[11px]">{iacConfig.iac_root_path}</code>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* IaC Configuration Form for GitLab */}
                  {showIaCConfig === repo.id && !iacConfig && (
                    <div className="mt-3 p-4 bg-muted/50 rounded-md border border-border">
                      <div className="mb-3 text-sm font-medium text-foreground">
                        Configure as IaC Repository
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium">IaC Type</label>
                          <Select
                            value={selectedIaCType}
                            onValueChange={setSelectedIaCType}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select IaC type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="terraform">Terraform</SelectItem>
                              <SelectItem value="cloudformation">CloudFormation</SelectItem>
                              <SelectItem value="pulumi">Pulumi</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-medium">Root Path</label>
                          <Input
                            value={selectedRootPath}
                            onChange={(e) => setSelectedRootPath(e.target.value)}
                            placeholder="e.g., /terraform or /"
                            className="mt-1"
                          />
                        </div>
                        {cloudConnections.length > 0 && (
                          <div>
                            <label className="text-xs font-medium">Cloud Account (Optional)</label>
                            <Select
                              value={selectedCloudConnection || 'none'}
                              onValueChange={(val) => setSelectedCloudConnection(val === 'none' ? '' : val)}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select cloud account" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {cloudConnections.map((conn) => (
                                  <SelectItem key={conn.id} value={conn.id}>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                        conn.type === 'aws' 
                                          ? 'bg-muted text-foreground' 
                                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      }`}>
                                        {conn.type.toUpperCase()}
                                      </span>
                                      {conn.displayName} ({conn.region})
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-2">
                          <Button
                            onClick={() => handleEnableIaC(repo.id)}
                            disabled={configuringIaC === repo.id}
                            size="sm"
                            className="bg-foreground text-background hover:bg-foreground/90"
                          >
                            {configuringIaC === repo.id ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Configuring...
                              </>
                            ) : (
                              <>Enable IaC</>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowIaCConfig(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
              })}

              {(!gitlabRepositories || !gitlabRepositories.repositories || gitlabRepositories.repositories.length === 0) && (
                <div className="text-center py-12 text-muted-foreground">
                  <svg className="h-16 w-16 mx-auto mb-4 opacity-40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.1 21.8l3.7-11.4h-7.4L12.1 21.8zM12.1 21.8l-3.7-11.4H1.6L12.1 21.8zM1.6 10.4L.2 14.6c-.1.4 0 .8.3 1l11.6 7.2l-10.5-12.4zM1.6 10.4h6.8l-2.2-6.8c-.2-.6-.8-.6-1 0L1.6 10.4zM12.1 21.8l3.7-11.4h6.8L12.1 21.8zM22.4 10.4l1.4 4.2c.1.4 0 .8-.3 1L12 22.8l10.4-12.4zM22.4 10.4h-6.8l2.2-6.8c.2-.6.8-.6 1 0l3.6 6.8z"/>
                  </svg>
                  <p className="text-lg font-medium mb-2">No repositories synced</p>
                  <p className="text-sm">Click "Add Projects" above to sync GitLab projects.</p>
                </div>
              )}
            </div>

            {gitlabRepositories && gitlabRepositories.repositories && gitlabRepositories.repositories.length > 0 && (
              <div className="text-xs text-muted-foreground text-center pt-2">
                {gitlabRepositories.repositories.length} repositories synced
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* GitLab Disconnect Confirmation Dialog */}
      <AlertDialog open={showDisconnectGitlabDialog} onOpenChange={setShowDisconnectGitlabDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Disconnect GitLab
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect GitLab? This will remove all synced repositories 
              and their IaC configurations. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnectingGitlab}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnectGitlab}
              disabled={disconnectingGitlab}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disconnectingGitlab ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CICDIntegration;